import dayjs from "dayjs";

export const formatDateRange = (value) => {
  if (!value || !Array.isArray(value) || value.length !== 2) {
    return "";
  }

  const [start, end] = value;
  const hasStart = start instanceof Date;
  const hasEnd = end instanceof Date;

  if (!hasStart && !hasEnd) return "";
  if (!hasStart && hasEnd) return "Till " + dateFormatter(end);
  if (hasStart && !hasEnd) return "From " + dateFormatter(start);
  return dateFormatter(start) + " - " + dateFormatter(end);
};

export const dateFormatter = (value, includeTime = false) => {
  if (!value || !dayjs(value).isValid()) return "";
  return dayjs(value).format(
    includeTime ? "MMM D, YYYY HH:mm:ss" : "MMM D, YYYY"
  );
};

export const renderDateRange = (value) => {
  if (!value || typeof value !== "string") return "—";

  const [start, end] = value.split(" - ").map((str) => str.trim());

  const startDate = dayjs(start);
  const endDate = dayjs(end);

  if (!startDate.isValid() || !endDate.isValid()) return value;

  return `${startDate.format("MMM D, YYYY")} - ${endDate.format("MMM D, YYYY")}`;
};

export const formatWithTimezone = (column, timezoneSuffix) => {
  const keysWithTimezone = [
    "bookingCreationTime",
    "cancellationCutOffLocal",
    "cancellationDate",
  ];

  // If input is an object (column case)
  if (column && typeof column === "object" && "dataKey" in column) {
    if (keysWithTimezone.includes(column.dataKey)) {
      const suffix = timezoneSuffix ? ` (${timezoneSuffix})` : "";
      return {
        ...column,
        label: column.label + suffix,
      };
    }
    return column;
  }

  return column;
};

/**
 * Checks if a date is in the past (before today's start of day)
 * @param {Date|string} date - The date to check
 * @param {boolean} allowPastDates - Whether past dates should be allowed
 * @returns {boolean} - Returns true if the date is in the past and past dates are not allowed
 */
export const isDateInPast = (date, allowPastDates = false) => {
  if (allowPastDates) {
    return false;
  }

  if (!date || !dayjs(date).isValid()) {
    return false;
  }

  return dayjs(date).isBefore(dayjs().startOf("day"));
};

/**
 * Validates if a date range exceeds the maximum allowed years
 * @param {Date} startDate - The start date of the range
 * @param {Date} endDate - The end date of the range
 * @param {number} maxYears - Maximum number of years allowed (default: 2)
 * @returns {boolean} - Returns true if the range exceeds the maximum years
 */
export const isDateRangeExceedingLimit = (startDate, endDate, maxYears = 2) => {
  if (!startDate || !endDate) {
    return false;
  }

  if (!dayjs(startDate).isValid() || !dayjs(endDate).isValid()) {
    return false;
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const maxEndDate = start.add(maxYears, "year");

  return end.isAfter(maxEndDate);
};

/**
 * Creates a function to disable dates beyond the maximum range from today
 * This is used with RSuite DateRangePicker's shouldDisableDate prop
 * @param {number} maxYears - Maximum number of years allowed from today (default: 2)
 * @param {function} additionalDisable - Additional disable function to combine with
 * @returns {function} - Function that returns true if a date should be disabled
 */
export const disableDatesBeyondMaxRange = (
  maxYears = 2,
  additionalDisable = null
) => {
  return (date) => {
    // Apply additional disable logic first (e.g., beforeToday)
    if (additionalDisable?.(date)) {
      return true;
    }

    if (!dayjs(date).isValid()) {
      return false;
    }

    const current = dayjs(date);
    const today = dayjs().startOf("day");
    const maxAllowedDate = today.add(maxYears, "year").subtract(1, "day");

    // Disable dates that are beyond maxYears from today
    return current.isAfter(maxAllowedDate);
  };
};
